import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Plus, Trash2, FileText, Building2, Ticket, Bell, Send, CheckCircle2, XCircle, Mail, MessageCircle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/lib/api';

export default function Settings() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifStatus, setNotifStatus] = useState(null);
  const [notifLog, setNotifLog] = useState([]);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);

  useEffect(() => {
    api.get('/settings').then((r) => setForm(r.data)).catch(() => toast.error('Erro ao carregar configurações')).finally(() => setLoading(false));
    loadNotif();
  }, []);

  const loadNotif = () => {
    api.get('/notifications/config').then((r) => {
      setNotifStatus(r.data);
      setCfg({
        smtp_host: r.data.smtp_host || '', smtp_port: r.data.smtp_port || '587',
        smtp_username: r.data.smtp_username || '', smtp_from: r.data.smtp_from || '',
        smtp_use_tls: !!r.data.smtp_use_tls, smtp_password: '',
        twilio_sid: r.data.twilio_sid || '', twilio_from: r.data.twilio_from || '',
        twilio_token: '', reminder_days: r.data.reminder_days || '7',
        has_smtp_password: !!r.data.has_smtp_password, has_twilio_token: !!r.data.has_twilio_token,
      });
    }).catch(() => {});
    api.get('/notifications/log').then((r) => setNotifLog(r.data)).catch(() => {});
  };

  const setCfgField = (k) => (e) => setCfg({ ...cfg, [k]: e.target.value });

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const payload = {
        smtp_host: cfg.smtp_host, smtp_port: cfg.smtp_port, smtp_username: cfg.smtp_username,
        smtp_from: cfg.smtp_from, smtp_use_tls: cfg.smtp_use_tls,
        twilio_sid: cfg.twilio_sid, twilio_from: cfg.twilio_from, reminder_days: cfg.reminder_days,
      };
      if (cfg.smtp_password) payload.smtp_password = cfg.smtp_password;
      if (cfg.twilio_token) payload.twilio_token = cfg.twilio_token;
      await api.put('/notifications/config', payload);
      toast.success('Credenciais salvas');
      loadNotif();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao salvar credenciais'); }
    finally { setSavingCfg(false); }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error('Informe um e-mail'); return; }
    setTesting(true);
    try {
      const r = await api.post('/notifications/test-email', { email: testEmail.trim() });
      if (r.data.ok) toast.success('E-mail de teste enviado!');
      else if (!r.data.email_configured) toast.error('SMTP não configurado (preencha as credenciais no ambiente)');
      else toast.error('Falha ao enviar e-mail de teste');
      loadNotif();
    } catch { toast.error('Erro ao enviar teste'); }
    finally { setTesting(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const updateTerm = (i, v) => { const t = [...(form.contract_terms || [])]; t[i] = v; setForm({ ...form, contract_terms: t }); };
  const addTerm = () => setForm({ ...form, contract_terms: [...(form.contract_terms || []), ''] });
  const removeTerm = (i) => setForm({ ...form, contract_terms: form.contract_terms.filter((_, idx) => idx !== i) });

  const updateTier = (i, k, v) => { const t = [...(form.cancellation_policy || [])]; t[i] = { ...t[i], [k]: v }; setForm({ ...form, cancellation_policy: t }); };
  const addTier = () => setForm({ ...form, cancellation_policy: [...(form.cancellation_policy || []), { label: '', reembolso: 0 }] });
  const removeTier = (i) => setForm({ ...form, cancellation_policy: form.cancellation_policy.filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        agency_name: form.agency_name, agency_phone: form.agency_phone,
        agency_email: form.agency_email, agency_address: form.agency_address,
        contract_title: form.contract_title,
        contract_terms: (form.contract_terms || []).filter((t) => t && t.trim()),
        cancellation_intro: form.cancellation_intro,
        cancellation_policy: (form.cancellation_policy || [])
          .filter((t) => t && (t.label || '').trim())
          .map((t) => ({ label: t.label, reembolso: Number(t.reembolso || 0) })),
        voucher_notes: form.voucher_notes,
      };
      const r = await api.put('/settings', payload);
      setForm(r.data);
      toast.success('Configurações salvas');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  if (loading) return <p className="text-muted-foreground">Carregando...</p>;
  if (!form) return <p className="text-muted-foreground">Não foi possível carregar.</p>;

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold">Configurações</h2>
          <p className="text-sm text-muted-foreground">Modelo de contrato, dados da agência e voucher.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="settings-save-button">{saving ? 'Salvando...' : <><Save className="h-4 w-4 mr-2" /> Salvar</>}</Button>
      </div>

      <Card className="p-4">
        <Tabs defaultValue="contrato">
          <TabsList>
            <TabsTrigger value="contrato" data-testid="settings-tab-contrato"><FileText className="h-4 w-4 mr-2" /> Contrato</TabsTrigger>
            <TabsTrigger value="agencia" data-testid="settings-tab-agencia"><Building2 className="h-4 w-4 mr-2" /> Agência</TabsTrigger>
            <TabsTrigger value="voucher" data-testid="settings-tab-voucher"><Ticket className="h-4 w-4 mr-2" /> Voucher</TabsTrigger>
            <TabsTrigger value="notificacoes" data-testid="settings-tab-notificacoes"><Bell className="h-4 w-4 mr-2" /> Notificações</TabsTrigger>
          </TabsList>

          <TabsContent value="contrato" className="pt-4 space-y-4">
            <div><Label>Título do contrato</Label><Input value={form.contract_title || ''} onChange={set('contract_title')} className="mt-1.5" data-testid="settings-contract-title" /></div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Cláusulas / Termos do contrato</Label>
                <Button type="button" variant="secondary" size="sm" onClick={addTerm} data-testid="settings-add-term"><Plus className="h-4 w-4 mr-1" /> Adicionar cláusula</Button>
              </div>
              <div className="space-y-3">
                {(form.contract_terms || []).length === 0 && <p className="text-sm text-muted-foreground">Nenhuma cláusula. Adicione itens ao modelo de contrato.</p>}
                {(form.contract_terms || []).map((t, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <span className="mt-2.5 text-sm text-muted-foreground w-5 text-right">{i + 1}.</span>
                    <Textarea value={t} onChange={(e) => updateTerm(i, e.target.value)} rows={2} className="flex-1" data-testid={`settings-term-${i}`} />
                    <Button type="button" variant="ghost" size="icon" className="mt-1" onClick={() => removeTerm(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">Estas cláusulas aparecem no PDF do contrato e na tela de assinatura do cliente.</p>
            </div>

            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Cláusula de rescisão e cancelamento</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-3">Reembolso progressivo conforme a antecedência em relação à data de início da viagem. O percentual incide sobre o valor total da viagem.</p>
              <div className="space-y-1.5">
                <Label>Texto introdutório</Label>
                <Textarea value={form.cancellation_intro || ''} onChange={set('cancellation_intro')} rows={3} data-testid="settings-cancellation-intro" />
              </div>
              <div className="flex items-center justify-between mt-4 mb-2">
                <Label>Faixas de reembolso</Label>
                <Button type="button" variant="secondary" size="sm" onClick={addTier} data-testid="settings-add-tier"><Plus className="h-4 w-4 mr-1" /> Adicionar faixa</Button>
              </div>
              <div className="space-y-3">
                {(form.cancellation_policy || []).length === 0 && <p className="text-sm text-muted-foreground">Nenhuma faixa definida.</p>}
                {(form.cancellation_policy || []).map((t, i) => (
                  <div key={i} className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs">Descrição da faixa</Label>
                      <Input value={t.label || ''} onChange={(e) => updateTier(i, 'label', e.target.value)} placeholder="Ex: Cancelamento com mais de 30 dias de antecedência" className="mt-1" data-testid={`settings-tier-label-${i}`} />
                    </div>
                    <div className="w-32">
                      <Label className="text-xs">Reembolso (%)</Label>
                      <Input type="number" min="0" max="100" value={t.reembolso ?? 0} onChange={(e) => updateTier(i, 'reembolso', e.target.value)} className="mt-1" data-testid={`settings-tier-refund-${i}`} />
                    </div>
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeTier(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">No contrato, cada faixa exibe o percentual e o valor correspondente em R$ calculado para a viagem.</p>
            </div>
          </TabsContent>

          <TabsContent value="agencia" className="pt-4 space-y-4">
            <div><Label>Nome da agência</Label><Input value={form.agency_name || ''} onChange={set('agency_name')} className="mt-1.5" data-testid="settings-agency-name" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Telefone</Label><Input value={form.agency_phone || ''} onChange={set('agency_phone')} className="mt-1.5" /></div>
              <div><Label>Email</Label><Input value={form.agency_email || ''} onChange={set('agency_email')} className="mt-1.5" /></div>
            </div>
            <div><Label>Endereço</Label><Input value={form.agency_address || ''} onChange={set('agency_address')} className="mt-1.5" /></div>
            <p className="text-xs text-muted-foreground">Estes dados aparecem no cabeçalho/rodapé do contrato e do voucher.</p>
          </TabsContent>

          <TabsContent value="voucher" className="pt-4 space-y-4">
            <div><Label>Observações do voucher</Label><Textarea value={form.voucher_notes || ''} onChange={set('voucher_notes')} rows={4} className="mt-1.5" data-testid="settings-voucher-notes" /></div>
            <p className="text-xs text-muted-foreground">Texto exibido na seção de observações do voucher de viagem.</p>
          </TabsContent>

          <TabsContent value="notificacoes" className="pt-4 space-y-5">
            <div>
              <Label className="text-base font-semibold">Status dos canais</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-3">Notificações automáticas são enviadas ao cliente: ao cadastrar a venda (viagem + contrato pendente), quando o contrato é assinado, e {notifStatus?.reminder_days || 7} dias antes da viagem.</p>
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm" data-testid="notif-status-email">
                  {notifStatus?.email_configured ? <CheckCircle2 className="h-4 w-4 text-[hsl(174_55%_34%)]" /> : <XCircle className="h-4 w-4 text-muted-foreground" />}
                  <span>E-mail (SMTP)</span>
                  <Badge variant={notifStatus?.email_configured ? 'default' : 'secondary'}>{notifStatus?.email_configured ? 'Configurado' : 'Não configurado'}</Badge>
                </div>
                <div className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm" data-testid="notif-status-whatsapp">
                  {notifStatus?.whatsapp_configured ? <CheckCircle2 className="h-4 w-4 text-[hsl(174_55%_34%)]" /> : <XCircle className="h-4 w-4 text-muted-foreground" />}
                  <span>WhatsApp (Twilio)</span>
                  <Badge variant={notifStatus?.whatsapp_configured ? 'default' : 'secondary'}>{notifStatus?.whatsapp_configured ? 'Configurado' : 'Não configurado'}</Badge>
                </div>
              </div>
              {!notifStatus?.email_configured && (
                <p className="text-xs text-muted-foreground mt-3">Preencha as credenciais de e-mail abaixo e clique em Salvar para ativar.</p>
              )}
            </div>

            {cfg && (
            <div className="border-t pt-4 space-y-4">
              <Label className="text-base font-semibold flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> Credenciais de E-mail (SMTP)</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Servidor (host)</Label><Input value={cfg.smtp_host} onChange={setCfgField('smtp_host')} placeholder="smtp.seuprovedor.com" className="mt-1.5" data-testid="cfg-smtp-host" /></div>
                <div><Label>Porta</Label><Input value={cfg.smtp_port} onChange={setCfgField('smtp_port')} placeholder="587 ou 465" className="mt-1.5" data-testid="cfg-smtp-port" /></div>
                <div><Label>Usuário</Label><Input value={cfg.smtp_username} onChange={setCfgField('smtp_username')} placeholder="usuario@dominio.com" className="mt-1.5" data-testid="cfg-smtp-username" /></div>
                <div><Label>Senha</Label><Input type="password" value={cfg.smtp_password} onChange={setCfgField('smtp_password')} placeholder={cfg.has_smtp_password ? '•••••••• (já configurada)' : 'senha / senha de app'} className="mt-1.5" data-testid="cfg-smtp-password" /></div>
                <div><Label>E-mail remetente</Label><Input value={cfg.smtp_from} onChange={setCfgField('smtp_from')} placeholder="contato@sandratur.com.br" className="mt-1.5" data-testid="cfg-smtp-from" /></div>
                <div className="flex items-center justify-between rounded-lg border px-3 mt-1.5">
                  <div><Label>Usar TLS/SSL direto</Label><p className="text-[11px] text-muted-foreground">Ligue para porta 465. Desligue para 587 (STARTTLS).</p></div>
                  <Switch checked={cfg.smtp_use_tls} onCheckedChange={(v) => setCfg({ ...cfg, smtp_use_tls: v })} data-testid="cfg-smtp-tls" />
                </div>
              </div>

              <Label className="text-base font-semibold flex items-center gap-2 pt-2"><MessageCircle className="h-4 w-4 text-primary" /> Credenciais de WhatsApp (Twilio)</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Account SID</Label><Input value={cfg.twilio_sid} onChange={setCfgField('twilio_sid')} placeholder="ACxxxxxxxx..." className="mt-1.5" data-testid="cfg-twilio-sid" /></div>
                <div><Label>Auth Token</Label><Input type="password" value={cfg.twilio_token} onChange={setCfgField('twilio_token')} placeholder={cfg.has_twilio_token ? '•••••••• (já configurado)' : 'token do Twilio'} className="mt-1.5" data-testid="cfg-twilio-token" /></div>
                <div className="sm:col-span-2"><Label>Número WhatsApp (from)</Label><Input value={cfg.twilio_from} onChange={setCfgField('twilio_from')} placeholder="whatsapp:+14155238886" className="mt-1.5" data-testid="cfg-twilio-from" /></div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Enviar lembrete quantos dias antes da viagem?</Label><Input type="number" min="0" value={cfg.reminder_days} onChange={setCfgField('reminder_days')} className="mt-1.5" data-testid="cfg-reminder-days" /></div>
              </div>

              <Button onClick={saveCfg} disabled={savingCfg} data-testid="cfg-save-button"><Save className="h-4 w-4 mr-2" /> {savingCfg ? 'Salvando...' : 'Salvar credenciais'}</Button>
              <p className="text-xs text-muted-foreground">Por segurança, senhas e tokens já salvos não são exibidos. Deixe o campo em branco para manter o valor atual.</p>
            </div>
            )}

            <div className="border-t pt-4">
              <Label>Enviar e-mail de teste</Label>
              <div className="flex gap-2 mt-1.5 max-w-md">
                <Input type="email" placeholder="seu-email@exemplo.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="notif-test-email-input" />
                <Button onClick={sendTest} disabled={testing} data-testid="notif-test-email-button"><Send className="h-4 w-4 mr-2" /> {testing ? 'Enviando...' : 'Testar'}</Button>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <Label>Histórico recente</Label>
                <Button variant="ghost" size="sm" onClick={loadNotif}>Atualizar</Button>
              </div>
              {notifLog.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhuma notificação registrada ainda.</p>
              ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="notif-log">
                  {notifLog.map((n, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border rounded-md px-3 py-1.5">
                      <span className="flex items-center gap-2">
                        <Badge variant="secondary" className="uppercase text-[10px]">{n.channel}</Badge>
                        <span className="text-muted-foreground">{({ new_sale: 'Nova venda', contract_signed: 'Contrato assinado', trip_reminder: 'Lembrete', test: 'Teste' })[n.event] || n.event}</span>
                        <span className="text-xs text-muted-foreground">{n.to}</span>
                      </span>
                      <Badge variant={n.status === 'sent' ? 'default' : (n.status === 'failed' ? 'destructive' : 'secondary')}>{n.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
